import React, { useEffect, useState, useMemo } from 'react';
import { useApi } from 'hooks/useApi';
import styles from './homepage.module.css';
import MoreThanPlatform from '../components/morethanaplatform/MoreThanPlatform';
import FlightCarousel from '../components/home/FlightCarousel';
import Certifications from './OurDrones/Certifications/Certifications';
import WhatsAppBot from '../components/WhatsAppBot/WhatsAppBot';

const ASSETS = {
  diagram_peregrine: '../Assets/Images/homepage/Diagram-Peregrine_1.png',
  droneinnovation: 'Assets/Images/home/droneinnovation.svg',
  dgca: 'Assets/Images/home/DGCA.svg'
};

const FEATURES_ARRAY = [
  'Integration',
  'AI Analytics', 
  'RPA Automations',
  'Advanced Schedulers',
  'Data Transformation',
];

const HomePage = () => {
  const CMS_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data: homePageData, loading, error } = useApi('/home-page');

  const FEATURE_DATA = useMemo(() => {
    const featureImage = homePageData?.success && homePageData?.home_image 
      ? `${CMS_BASE_URL}/attachments/${homePageData.home_image}`
      : '../Assets/Images/homepage/dronemage1.svg';

    return [{
      label: '',
      description: "Highest standards of security and safety, with secure firmware development that is digitally signed and equipped with advanced security measures to guarantee safe and efficient operation across every mission.",
      image: featureImage,
    }];
  }, [homePageData, CMS_BASE_URL]);

  // Hash scroll
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Text rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % FEATURES_ARRAY.length);
      }, 500);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className={styles.homePage}>
        <div className={styles.loadingState}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    console.error('HomePage API error:', error);
  }

  return (
    <div className={styles.homePage}>
      {/* Header */}
      <header className={styles.headerSection}>
        <div className={styles.heroSection}>
          <FlightCarousel />
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.mainContent}>
        {/* Introduction */}
        <section className={styles.introSection}>
          <div className={styles.container}>
            <h1 className={styles.mainTitle}>
              axldrone – Defining the Future of Industrial Aerial Innovation
            </h1>
            <p className={styles.mainDescription}>
            At the forefront of industrial drone technology, axldrone sets the standard with purpose-built aerial platforms, state-of-the-art avionics, and intelligent ground control solutions. Backed by over a decade of global R&D and mission-proven expertise, every system reflects our unwavering commitment to performance, reliability, and innovation. From design to deployment, axldrone delivers elite, end-to-end drone solutions tailored for the world's most demanding industrial operations.
            </p>
          </div>
        </section>

        {/* Innovation Image */}
        <section className={styles.innovationSection}>
          <div className={styles.container}>
            <img
              src={ASSETS.droneinnovation}
              alt="Drone Innovation Technology"
              className={styles.innovationImage}
              width="800"
              height="600"
              loading="lazy"
            />
          </div>
        </section>

        {/* Features */}
        <section className={styles.featuresSection}>
          <div className={styles.container}>
            {FEATURE_DATA.map((feature, index) => (
              <article
                key={`feature-${index}`}
                className={`${styles.featureItem} ${
                  index % 2 === 0 ? styles.imageRight : styles.imageLeft
                }`}
              >
                <div className={styles.featureContent}>
                  {feature.label && (
                    <h2 className={styles.featureLabel}>{feature.label}</h2>
                  )}
                  <p className={styles.featureDescription}>{feature.description}</p>
                </div>

                <div className={styles.featureImageContainer}>
                  <div className={styles.decorativeCircle1}></div>
                  <div className={styles.decorativeCircle2}></div>
                  <img
                    src={feature.image}
                    alt={feature.label || 'Security and Safety Features'}
                    className={styles.featureImage}
                    width="400"
                    height="400"
                    loading="lazy"
                  />
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Platform */}
        <section className={styles.platformSection}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Powerful Software</h2>
            <MoreThanPlatform />
          </div>
        </section>

        {/* Diagram */}
        <section className={styles.diagramSection}>
          <div className={styles.container}>
            <img
              src={ASSETS.diagram_peregrine}
              alt="Peregrine System Architecture Diagram"
              className={styles.diagramImage}
              width="500"
              height="auto"
              loading="lazy"
            />
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className={styles.footerSection}>
        <Certifications />
      </footer>

      {/* WhatsApp Bot  */}
      <WhatsAppBot />
    </div>
  );
};

export default HomePage;